import type { UpdateOptions } from 'react-toastify';
import { toast } from 'react-toastify';

import { waitForTransactionReceipt } from '@wagmi/core';

import type { TransactionReceipt } from 'viem';

import type { AsyncTransactionResult } from '@api';

// eslint-disable-next-line deprecate/import
import { wagmiConfig } from '@shared/common/providers/LegacyWeb3Provider/wagmi';
import { getChainById } from '@shared/helpers/web3/chain';
import truncate from '@shared/helpers/web3/truncate';

import ToastButton from '../../common/components/ToastButton';
import ToastLink from '../../common/components/ToastLink';
import { copyToClipboardWithToast } from '../copyToClipboardWithToast';
import { normalizeError } from '../displayErrorToast';

export interface TransactionReceiptWithLinkToExplorer extends TransactionReceipt {
  linkToBlockExplorer?: string;
}

// TODO: Remove side effect of "Toast" and .tsx -> .ts, and make smth like trackAndNotifyTransactionStatusWithToast
export async function trackAndNotifyTransactionStatus(
  hash: Awaited<AsyncTransactionResult>,
  chainId: number,
  confirmations = 2,
): Promise<TransactionReceiptWithLinkToExplorer | undefined> {
  if (!hash) {
    toast.error('Invalid transaction');
    return;
  }

  const chain = getChainById(chainId);
  const blockExplorerHost = chain?.blockExplorers?.default?.url;
  const linkToBlockExplorer = `${blockExplorerHost}/tx/${hash}`;

  // Show transaction hash and call 'wait()' to wait the transaction to finish
  toast.info(
    ({ toastProps }) => (
      <div className="flex flex-col gap-2">
        <b>Transaction Hash:</b>
        <i>{hash}</i>

        <ToastButton
          closeOnClick={false}
          toastId={toastProps.toastId}
          className="bg-primary-600 text-neutral-50"
          onClick={() => copyToClipboardWithToast(hash)}
        >
          Copy
        </ToastButton>

        {blockExplorerHost && (
          <ToastLink
            href={linkToBlockExplorer}
            rel="noreferrer "
            target="_blank"
            variant="ghost"
            closeOnClick={false}
            toastId={toastProps.toastId}
            className="text-neutral-50 font-normal"
          >
            View on block explorer
          </ToastLink>
        )}
      </div>
    ),
    {
      autoClose: 15000,
      closeOnClick: false,
    },
  );

  const toastId = Date.now();
  const toastMessage = 'Waiting for transaction to finish...';

  toast.loading(toastMessage, { toastId });

  const commonToastUpdateOptions: UpdateOptions = {
    type: 'error',
    render: `Transaction failed: ${truncate(hash, { start: 5, end: 5 })}`,
    autoClose: 5000,
    isLoading: false,
  };

  try {
    const receipt = (await waitForTransactionReceipt(wagmiConfig, {
      hash,
      timeout: 300_000, // 5 min
      chainId,
      confirmations,
      onReplaced: (newTransaction) => console.debug('onTxReplaced', { newTransaction }),
    })) as TransactionReceiptWithLinkToExplorer;

    toast.update(toastId, {
      ...commonToastUpdateOptions,
      ...(receipt?.status
        ? {
            type: 'success',
            render: `Transaction finished: ${truncate(hash, {
              start: 5,
              end: 5,
            })}`,
          }
        : {}),
    });

    receipt.linkToBlockExplorer = linkToBlockExplorer;

    return receipt;
  } catch (error: any) {
    const normalizedError = normalizeError(error);

    // Replace error to custom text when user get timed out error from mainnet
    // this need because transaction is done but user get error from waitForTransactionReceipt
    const ReplacedErrorForTimedOut = () => (
      <div className="flex flex-col">
        We haven't received a confirmation from the Blockchain about the completion of your
        transaction. Please use the following link to check its status:
        {blockExplorerHost && (
          <ToastLink
            href={linkToBlockExplorer}
            rel="noreferrer "
            target="_blank"
            variant="ghost"
            closeOnClick={false}
            toastId={toastId}
            className="text-neutral-50 font-normal"
          >
            View transaction
          </ToastLink>
        )}
      </div>
    );

    const isTimedOutError = normalizedError.message.includes(
      'Timed out while waiting for transaction with hash',
    );
    const errorMessage = isTimedOutError ? ReplacedErrorForTimedOut : normalizedError.message;

    const toastOptions = {
      ...commonToastUpdateOptions,
      render: errorMessage,
      ...(isTimedOutError && { type: 'warning' as const }),
    };

    toast.update(toastId, toastOptions);
  }
}
